import {
	HoverCard,
	HoverCardType,
	Label,
	Link,
	mergeStyleSets,
	Separator,
	Shimmer,
	Stack,
	Theme,
	useTheme,
} from '@fluentui/react';
import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {createSearchParams, useNavigate} from 'react-router-dom';
import {useUserContext} from 'authentication/UserContext';

import {v4 as uuidv4} from 'uuid';
import {
	PogisHoverContent,
	RegulationHoverContent,
	RegulatoryDocumentHoverContent,
	RequirementHoverContent,
} from './TextSearchHoverCards';
import {checkSearchValid} from './TextSearchBar';

const NUM_TOP_SUGG = 5;
const NUM_MIN_SEARCH_CHAR = 3;

type Suggestion = {
	display: JSX.Element;
	link: string;
	id?: string;
	searchText?: string;
	newTab?: boolean;
	page?: string;
};

type TextSearchBarSuggestionsProps = {
	searchText: string;
	loadingSuggestionsReq: boolean;
	loadingSuggestionsReg: boolean;
	loadingSuggestionsRegDoc: boolean;
	loadingSuggestionsPOGIS: boolean;
	suggestionsAI: Suggestion[];
	suggestionsReq: Suggestion[];
	suggestionsReg: Suggestion[];
	suggestionsRegDoc: Suggestion[];
	suggestionsPOGIS: Suggestion[];
	suggestionsFooter: Suggestion[];
	selectedSuggestion: Suggestion | undefined;
	setHideSuggestions: (hideSuggestions: boolean) => void;
	setSearchText: (searchText: string) => void;
};

const nsTranslation = 'components/textsearchbar';

export const TextSearchBarSuggestions: React.FC<
	TextSearchBarSuggestionsProps
> = props => {
	const {
		searchText,
		loadingSuggestionsReq,
		loadingSuggestionsReg,
		loadingSuggestionsRegDoc,
		loadingSuggestionsPOGIS,
		suggestionsAI,
		suggestionsReq,
		suggestionsReg,
		suggestionsRegDoc,
		suggestionsPOGIS,
		suggestionsFooter,
		selectedSuggestion,
		setHideSuggestions,
		setSearchText,
	} = props;

	const {t} = useTranslation(nsTranslation);
	const navigate = useNavigate();
	const theme = useTheme();
	const classNames = getClassNames(theme);
	const {isAvk, isBav} = useUserContext();
	const isAvkOrBav = isAvk || isBav;

	const handleClick = (suggestion: Suggestion) => () => {
		if (suggestion.newTab === true) {
			window.open(suggestion.link);
			setHideSuggestions(false);
		} else {
			navigate(suggestion.link);
			setHideSuggestions(true);
		}
		setSearchText(suggestion.searchText ?? '');
	};

	const handleMoreResult = (text: string, entity: string) => () => {
		navigate({
			pathname: 'search',
			search: createSearchParams({
				entity: entity.toLowerCase(),
				text,
			}).toString(),
		});
		setHideSuggestions(true);
		setSearchText(text);
	};

	const paramsRenderFunction = useMemo(() => {
		return {
			searchText,
			selectedSuggestion,
			handleClick,
			handleMoreResult,
		};
	}, [searchText, selectedSuggestion]);

	const SearchSuggestionComponent = useMemo(
		() => (
			<>
				{suggestionsAI.map(suggestion => {
					const handleSuggestionHover = () => (
						<div style={{padding: 10}}>
							{t('SearchFor', {searchText: suggestion.searchText})}
						</div>
					);
					return (
						<TextSearchBarSuggestion
							key={uuidv4()}
							suggestion={suggestion}
							handleSuggestionHover={handleSuggestionHover}
							{...paramsRenderFunction}
						/>
					);
				})}
				<Separator />
			</>
		),
		[suggestionsAI, paramsRenderFunction],
	);

	const ReqSuggestionsComponent = useMemo(() => {
		const handleEntityHover = (id: string) => (
			<RequirementHoverContent id={id} />
		);
		const propsEntity = {
			loading: loadingSuggestionsReq,
			suggestions: suggestionsReq,
			entity: 'Requirements',
			handleEntityHover,
			...paramsRenderFunction,
		};
		return <TextSearchBarEntitySuggestion {...propsEntity} />;
	}, [suggestionsReq, loadingSuggestionsReq, paramsRenderFunction]);

	const RegSuggestionsComponent = useMemo(() => {
		const handleEntityHover = (id: string) => (
			<RegulationHoverContent id={id} />
		);
		const propsEntity = {
			loading: loadingSuggestionsReg,
			suggestions: suggestionsReg,
			entity: 'Regulations',
			handleEntityHover,
			...paramsRenderFunction,
		};
		return <TextSearchBarEntitySuggestion {...propsEntity} />;
	}, [suggestionsReg, loadingSuggestionsReg, paramsRenderFunction]);

	const RegDocSuggestionsComponent = useMemo(() => {
		const handleEntityHover = (id: string) => (
			<RegulatoryDocumentHoverContent id={id} />
		);
		const propsEntity = {
			loading: loadingSuggestionsRegDoc,
			suggestions: suggestionsRegDoc,
			entity: 'RegulatoryDocuments',
			handleEntityHover,
			...paramsRenderFunction,
		};
		return <TextSearchBarEntitySuggestion {...propsEntity} />;
	}, [suggestionsRegDoc, loadingSuggestionsRegDoc, paramsRenderFunction]);

	const POGISSuggestionsComponent = useMemo(() => {
		const handleEntityHover = (id: string) => <PogisHoverContent id={id} />;
		const propsEntity = {
			loading: loadingSuggestionsPOGIS,
			suggestions: suggestionsPOGIS,
			entity: 'PogisDocuments',
			handleEntityHover,
			...paramsRenderFunction,
		};
		return <TextSearchBarEntitySuggestion {...propsEntity} />;
	}, [suggestionsPOGIS, loadingSuggestionsPOGIS, paramsRenderFunction]);

	const NaviSuggestionComponent = useMemo(
		() =>
			suggestionsFooter.map(suggestion => {
				const handleSuggestionHover = () => (
					<div style={{padding: 10}}>
						{t('NavigateTo', {page: suggestion.page})}
					</div>
				);
				return (
					<TextSearchBarSuggestion
						key={uuidv4()}
						suggestion={suggestion}
						handleSuggestionHover={handleSuggestionHover}
						{...paramsRenderFunction}
					/>
				);
			}),
		[suggestionsFooter, paramsRenderFunction],
	);

	return (
		<Stack className={classNames.suggestions} tokens={{childrenGap: 2}}>
			{checkSearchValid(searchText) && (
				<>
					{SearchSuggestionComponent}
					{!isAvkOrBav && ReqSuggestionsComponent}
					{RegSuggestionsComponent}
					{RegDocSuggestionsComponent}
					{!isAvkOrBav && POGISSuggestionsComponent}
				</>
			)}
			{NaviSuggestionComponent}
		</Stack>
	);
};

const TextSearchBarSuggestion: React.FC<{
	suggestion: Suggestion;
	selectedSuggestion: Suggestion | undefined;
	handleClick: (suggestion: Suggestion) => () => void;
	handleSuggestionHover?: () => JSX.Element;
}> = props => {
	const {suggestion, selectedSuggestion, handleClick, handleSuggestionHover} =
		props;

	const theme = useTheme();
	const classNames = getClassNames(theme);

	const styleSelectedSuggestion = {
		background: `${theme.palette.neutralLight}cc`,
	};

	const cardProps = {
		onRenderPlainCard:
			handleSuggestionHover ||
			(() => {
				return <>No Implementation</>;
			}),
	};

	return (
		<Stack.Item
			className={classNames.resultItem}
			style={
				selectedSuggestion?.link === suggestion.link
					? styleSelectedSuggestion
					: undefined
			}
			onClick={handleClick(suggestion)}
		>
			<HoverCard
				key={uuidv4()}
				type={HoverCardType.plain}
				plainCardProps={cardProps}
				cardOpenDelay={100}
			>
				<span style={{paddingLeft: 10}}>{suggestion.display}</span>
			</HoverCard>
		</Stack.Item>
	);
};

const TextSearchBarEntitySuggestion: React.FC<{
	searchText: string;
	entity: string;
	loading: boolean;
	suggestions: Suggestion[];
	selectedSuggestion: Suggestion | undefined;
	handleClick: (suggestion: Suggestion) => () => void;
	handleMoreResult: (text: string, entity: string) => () => void;
	handleEntityHover: (id: string) => JSX.Element;
}> = props => {
	const {
		searchText,
		entity,
		loading,
		suggestions,
		handleMoreResult,
		handleEntityHover,
	} = props;

	const {t} = useTranslation(nsTranslation);
	const theme = useTheme();
	const classNames = getClassNames(theme);

	return (
		<>
			<Label>{t(entity)}</Label>
			{loading ? (
				<Stack tokens={{childrenGap: 5}}>
					<Shimmer />
					<Shimmer />
					<Shimmer />
				</Stack>
			) : (
				suggestions.map(suggestion => (
					<TextSearchBarSuggestion
						key={uuidv4()}
						suggestion={suggestion}
						handleSuggestionHover={() => handleEntityHover(suggestion.id!)}
						{...props}
					/>
				))
			)}
			<div className={classNames.moreButtonWrapper}>
				{suggestions.length > 0 && (
					<Link onClick={handleMoreResult(searchText, entity)}>
						{t('MoreResults')}
					</Link>
				)}
			</div>

			<Separator />
		</>
	);
};

export const getClassNames = (theme: Theme) =>
	mergeStyleSets({
		componentWrapper: {
			width: 600,
			zIndex: 100,
			paddingRight: '50px',
			position: 'relative',
			top: '50%',
			'-webkit-transform': 'translateY(-50%)',
			'-ms-transform': 'translateY(-50%)',
			transform: 'translateY(-50%)',
		},
		suggestions: {
			background: theme.palette.white,
			position: 'absolute',
			zIndex: 10,
			width: 600,
			top: '35px',
			padding: '16px',
			boxShadow: `0px 3px 10px ${theme.palette.neutralPrimary}`,
			maxHeight: 'calc(100vh - 100px)',
			overflowY: 'auto',
		},
		resultItem: {
			padding: '4px 0',
			cursor: 'pointer',
			':hover': {
				background: `${theme.palette.neutralLight}cc`,
			},
		},
		moreButtonWrapper: {
			textAlign: 'right',
			paddingRight: 10,
		},
	});
